import { useEffect, useMemo, useState } from "react";
import { PARTNER_QUOTATION, PARTNER } from "../../../api";
import { format, parse } from "date-fns";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const options = {
	responsive: true,
	plugins: {
		legend: {
			position: "top",
		},
		title: {
			display: true,
			text: "Monthly Sales Data",
		},
	},
	scales: {
		y: {
			// 두 개의 y 축 정의
			type: "linear",
			display: true,
			position: "left",
		},
		y1: {
			// 두 번째 y 축 정의
			type: "linear",
			display: true,
			position: "right",
			grid: {
				drawOnChartArea: false, // 두 번째 y 축에 대한 그리드 라인을 그리지 않습니다.
			},
		},
	},
};

export default function PartnerMySaleStatusPage() {
	const [startDate, setStartDate] = useState<string | undefined>(undefined);
	const [endDate, setEndDate] = useState<string | undefined>(undefined);
	const [count, setCount] = useState(0);
	const [selectedDate, setSelectedDate] = useState<string | null>(null);

	const [data, setData] = useState<any[]>([]);
	const [me, setMe] = useState<any>(null);

	useEffect(() => {
		PARTNER.getMe().then((data: any) => {
			setMe(data);
		});
	}, []);

	useEffect(() => {
		if (me) {
			fetch();
		}
	}, [me, startDate, endDate]);

	useEffect(() => {
		if (selectedDate === "1주") {
			const start = new Date();
			const end = new Date();
			start.setDate(start.getDate() - 7);
			setStartDate(format(start, "yyyy-MM-dd"));
			setEndDate(format(end, "yyyy-MM-dd"));
		} else if (selectedDate === "1개월") {
			const start = new Date();
			const end = new Date();
			start.setMonth(start.getMonth() - 1);
			setStartDate(format(start, "yyyy-MM-dd"));
			setEndDate(format(end, "yyyy-MM-dd"));
		} else if (selectedDate === "3개월") {
			const start = new Date();
			const end = new Date();
			start.setMonth(start.getMonth() - 3);
			setStartDate(format(start, "yyyy-MM-dd"));
			setEndDate(format(end, "yyyy-MM-dd"));
		} else if (selectedDate === "전체") {
			setStartDate(undefined);
			setEndDate(undefined);
		}
	}, [selectedDate]);

	const fetch = async () => {
		PARTNER_QUOTATION.getStatistics(me.id, startDate, endDate).then((data: any) => {
			setData(data);
			setCount(data.length);
		});
	};

	const filtered = useMemo(() => {
		if (data.length === 0) {
			return [];
		}

		const minDate = data.reduce((acc, cur) => {
			return acc < cur.monthYear ? acc : cur.monthYear;
		}, data[0].monthYear);

		const start = new Date(minDate);
		const end = new Date();

		const months = [];
		let dt = start;

		while (dt <= end) {
			months.push(format(dt, "yyyy-MM"));
			dt.setMonth(dt.getMonth() + 1);
		}

		return months.map((month) => {
			return {
				month,
				totalAmount: data.find((item) => item.monthYear === month)?.totalAmount,
				totalQuantity: data.find((item) => item.monthYear === month)?.count,
			};
		});
	}, [data, selectedDate]);

	const dates = [
		{ label: "1주", value: "1주" },
		{ label: "1개월", value: "1개월" },
		{ label: "3개월", value: "3개월" },
		{ label: "전체", value: "전체" },
	];

	const chartData = useMemo(() => {
		// ... useMemo 로직 ...

		// Chart.js 데이터 구조 준비
		return {
			labels: filtered.map((v) => v.month), // 모든 월 배열
			datasets: [
				{
					label: "총 주문 금액",
					data: filtered.map((v) => v.totalAmount ?? 0),
					borderColor: "rgb(255, 99, 132)",
					backgroundColor: "rgba(255, 99, 132, 0.5)",
					yAxisID: "y",
				},
				{
					label: "총 주문 수량",
					data: filtered.map((v) => v.totalQuantity ?? 0),
					borderColor: "rgb(53, 162, 235)",
					backgroundColor: "rgba(53, 162, 235, 0.5)",
					yAxisID: "y1",
				},
			],
		};
	}, [data]); // 의존성 배열에 data 포함

	return (
		<div className="flex justify-center">
			<div className="w-full max-w-lg">
				<div className="text-success flex px-3 gap-1 mt-5 font-medium">{Icon} 판매현황</div>
				<div className="px-3 mt-6">
					<div className="h-[1px] w-full bg-[#dadada]"></div>
				</div>

				<div className="mt-5 font-medium text-sm text-black px-3">기간별 조회</div>
				<div className="px-3 flex gap-2 mt-2.5">
					<input
						value={startDate}
						onChange={(v) => setStartDate(v.target.value)}
						type="date"
						placeholder=" "
						className="rounded-sm bg-transparent text-xs font-normal text-black border border-[#dadada] px-2.5 h-[30px]"
					/>
					~
					<input
						value={endDate}
						onChange={(v) => setEndDate(v.target.value)}
						type="date"
						placeholder=" "
						className="rounded-sm bg-transparent text-xs font-normal text-black border border-[#dadada] px-2.5 h-[30px]"
					/>
					<div className="text-xs text-success font-normal flex-none h-[30px] w-[78px] border border-[#dadada] flex justify-center items-center">{count}건</div>
				</div>
				<div className="px-3 mt-2.5 flex gap-2.5">
					{dates.map((item) => {
						const isSelected = selectedDate === item.value;
						return (
							<div
								onClick={() => setSelectedDate(item.value)}
								className={[
									"w-20 text-xs font-normal flex justify-center items-center border py-1.5 rounded-sm",
									isSelected ? "border-success text-success" : "border-[#dadada] text-black",
								].join(" ")}>
								{item.label}
							</div>
						);
					})}
				</div>
				<div className="mt-5 px-3">
					<Line
						options={{
							responsive: true,
							scales: {
								y: {
									// 두 개의 y 축 정의
									type: "linear",
									display: true,
									position: "left",
								},
								y1: {
									// 두 번째 y 축 정의
									type: "linear",
									display: true,
									position: "right",
									grid: {
										drawOnChartArea: false, // 두 번째 y 축에 대한 그리드 라인을 그리지 않습니다.
									},
								},
							},
						}}
						data={chartData}
					/>
				</div>

				<div className="border-b border-[#dadada] my-5"></div>

				<div className="px-3">
					<table className="w-full table-fixed border border-[#dadada]">
						<thead className="border-b border-[#dadada]">
							<th className="py-3"></th>
							<th className="py-3">월별 건수</th>
							<th className="py-3">금액</th>
						</thead>
						<tbody>
							{filtered.map((item: any) => {
								return (
									<tr>
										<td className="text-center py-1.5 px-2  font-normal border-l border-r border-[#dadada]">{item.month}</td>
										<td className="text-center py-1.5 px-2 font-normal border-l border-r  border-[#dadada]">{item.totalQuantity ?? 0}개</td>
										<td className="text-center py-1.5 px-2 font-normal border-l border-r border-[#dadada]">{item.totalAmount ?? 0}원</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}

const Icon = (
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
		<path
			d="M7.80782 19.7494C7.41452 19.8794 7.20106 20.3036 7.33104 20.6969C7.46101 21.0902 7.88521 21.3036 8.2785 21.1737L7.80782 19.7494ZM13.8622 20.8462L13.7947 20.0992H13.7947L13.8622 20.8462ZM17.3536 20.0769L17.6802 20.7521L17.3536 20.0769ZM20.6897 17.0681L20.1089 16.5936V16.5936L20.6897 17.0681ZM18.408 15.1854L18.936 15.718L18.408 15.1854ZM16.8715 16.7087L17.3996 17.2413L16.8715 16.7087ZM12.6984 16.6346C12.2842 16.6346 11.9484 16.9704 11.9484 17.3846C11.9484 17.7988 12.2842 18.1346 12.6984 18.1346V16.6346ZM20.335 14.9962L19.9214 15.6219L20.335 14.9962ZM9.22402 14.4915L9.55713 15.1635H9.55713L9.22402 14.4915ZM13.4001 14.1128L13.5958 13.3888V13.3888L13.4001 14.1128ZM15.0309 14.5538L14.8351 15.2778H14.8351L15.0309 14.5538ZM15.5476 16.4829L15.0196 15.9502L15.0196 15.9502L15.5476 16.4829ZM14.11 16.852C13.8159 17.1436 13.8138 17.6185 14.1055 17.9127C14.3971 18.2068 14.872 18.2089 15.1661 17.9172L14.11 16.852ZM8.2785 21.1737C8.42441 21.1254 8.69032 21.1019 9.1177 21.1321C9.52982 21.1613 10.0083 21.2326 10.5463 21.3164C11.5786 21.4771 12.8472 21.6909 13.9297 21.5931L13.7947 20.0992C12.9375 20.1766 11.8785 20.0057 10.7771 19.8342C10.2483 19.7519 9.70847 19.6702 9.22349 19.6359C8.75377 19.6027 8.24381 19.6053 7.80782 19.7494L8.2785 21.1737ZM13.9297 21.5931C15.4406 21.4566 16.1472 21.4938 17.6802 20.7521L17.027 19.4018C15.783 20.0036 15.361 19.9577 13.7947 20.0992L13.9297 21.5931ZM17.6802 20.7521C19.0548 20.087 20.4335 18.5672 21.2706 17.5425L20.1089 16.5936C19.2646 17.6271 18.0697 18.8973 17.027 19.4018L17.6802 20.7521ZM17.88 14.6528L16.3435 16.1761L17.3996 17.2413L18.936 15.718L17.88 14.6528ZM15.2257 16.6346H12.6984V18.1346H15.2257V16.6346ZM16.3435 16.1761C16.0481 16.469 15.6461 16.6346 15.2257 16.6346V18.1346C16.0398 18.1346 16.8219 17.814 17.3996 17.2413L16.3435 16.1761ZM20.7485 14.3705C19.8472 13.7748 18.6475 13.8918 17.88 14.6528L18.936 15.718C19.198 15.4583 19.6113 15.4169 19.9214 15.6219L20.7485 14.3705ZM21.2706 17.5425C22.1102 16.5147 21.7952 15.0623 20.7485 14.3705L19.9214 15.6219C20.2902 15.8656 20.3399 16.3108 20.1089 16.5936L21.2706 17.5425ZM3.77587 14.2885H6.87935V12.7885H3.77587V14.2885ZM6.90522 14.3077V21.2308H8.40522V14.3077H6.90522ZM6.87935 21.25H3.77587V22.75H6.87935V21.25ZM3.75 21.2308V14.3077H2.25V21.2308H3.75ZM3.77587 21.25C3.76999 21.25 3.76642 21.249 3.7639 21.2479C3.76091 21.2466 3.75789 21.2447 3.75529 21.2421C3.75269 21.2395 3.75138 21.2372 3.75082 21.2359C3.75048 21.2351 3.75 21.2339 3.75 21.2308H2.25C2.25 22.0759 2.93925 22.75 3.77587 22.75V21.25ZM6.90522 21.2308C6.90522 21.2339 6.90475 21.2351 6.9044 21.2359C6.90384 21.2372 6.90253 21.2395 6.89993 21.2421C6.89733 21.2447 6.89431 21.2466 6.89132 21.2479C6.8888 21.249 6.88523 21.25 6.87935 21.25V22.75C7.71597 22.75 8.40522 22.0759 8.40522 21.2308H6.90522ZM6.87935 14.2885C6.88523 14.2885 6.8888 14.2895 6.89132 14.2906C6.89431 14.2918 6.89733 14.2938 6.89993 14.2964C6.90253 14.2989 6.90384 14.3013 6.9044 14.3026C6.90475 14.3034 6.90522 14.3046 6.90522 14.3077H8.40522C8.40522 13.4626 7.71598 12.7885 6.87935 12.7885V14.2885ZM3.77587 12.7885C2.93925 12.7885 2.25 13.4626 2.25 14.3077H3.75C3.75 14.3046 3.75048 14.3034 3.75082 14.3026C3.75138 14.3013 3.75269 14.2989 3.75529 14.2964C3.7579 14.2938 3.76091 14.2918 3.76391 14.2906C3.76642 14.2895 3.76999 14.2885 3.77587 14.2885V12.7885ZM8.37627 15.7489L9.55713 15.1635L8.89091 13.8196L7.71005 14.405L8.37627 15.7489ZM11.6529 14.6731H11.9711V13.1731H11.6529V14.6731ZM13.2043 14.8368L14.8351 15.2778L15.2267 13.8298L13.5958 13.3888L13.2043 14.8368ZM15.0196 15.9502L14.11 16.852L15.1661 17.9172L16.0756 17.0155L15.0196 15.9502ZM14.8351 15.2778C15.1454 15.3617 15.2364 15.7353 15.0196 15.9502L16.0756 17.0155C17.1225 15.9775 16.6406 14.2121 15.2267 13.8298L14.8351 15.2778ZM11.9711 14.6731C12.3877 14.6731 12.8025 14.7282 13.2043 14.8368L13.5958 13.3888C13.0663 13.2456 12.5199 13.1731 11.9711 13.1731V14.6731ZM9.55713 15.1635C10.2075 14.8411 10.9251 14.6731 11.6529 14.6731V13.1731C10.6944 13.1731 9.74877 13.3943 8.89091 13.8196L9.55713 15.1635ZM16.9915 8.15385C16.9915 9.43291 15.9439 10.4808 14.6381 10.4808V11.9808C16.7602 11.9808 18.4915 10.2735 18.4915 8.15385H16.9915ZM14.6381 10.4808C13.3322 10.4808 12.2846 9.43291 12.2846 8.15385H10.7846C10.7846 10.2735 12.5159 11.9808 14.6381 11.9808V10.4808ZM12.2846 8.15385C12.2846 6.87479 13.3322 5.82692 14.6381 5.82692V4.32692C12.5159 4.32692 10.7846 6.03423 10.7846 8.15385H12.2846ZM14.6381 5.82692C15.9439 5.82692 16.9915 6.87479 16.9915 8.15385H18.4915C18.4915 6.03423 16.7602 4.32692 14.6381 4.32692V5.82692ZM11.5346 7.40385C10.2287 7.40385 9.18109 6.35598 9.18109 5.07692H7.68109C7.68109 7.19654 9.41245 8.90385 11.5346 8.90385V7.40385ZM9.18109 5.07692C9.18109 3.79786 10.2287 2.75 11.5346 2.75V1.25C9.41245 1.25 7.68109 2.95731 7.68109 5.07692H9.18109ZM11.5346 2.75C12.8405 2.75 13.8881 3.79786 13.8881 5.07692H15.3881C15.3881 2.95731 13.6567 1.25 11.5346 1.25V2.75Z"
			fill="#0057FF"
		/>
	</svg>
);
